import React, { useState, useEffect } from 'react';
import { Card, CardContent } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { subdivisionesAPI } from '../utils/api';
import { toast } from 'sonner';
import { Plus, Edit, Trash2, Building, Layers, Shield, Loader2, Calendar, Copy } from 'lucide-react';
import { Badge } from './ui/badge';

const TIPOS_SUBDIVISION = ['Bloque', 'Edificio', 'Etapa', 'Manzana', 'Zona'];

const SubdivisionesManager = ({ proyecto, onUpdate }) => {
  const [subdivisiones, setSubdivisiones] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showDialog, setShowDialog] = useState(false);
  const [editingSubdivision, setEditingSubdivision] = useState(null);
  const [saving, setSaving] = useState(false);
  const [copyingFecha, setCopyingFecha] = useState(null);

  const initialFormData = {
    nombre: '',
    codigo: proyecto?.codigo_proyecto ? `${proyecto.codigo_proyecto}-` : '',
    tipo_subdivision: '',
    numero_pisos: '',
    observaciones: '',
    padre_id: '',
    fecha_entrega: '',
    fecha_inicio_garantia_acabados: '',
    garantia_acabados_anios: 1,
    garantia_acabados_meses: 0,
    fecha_inicio_garantia_estructural: '',
    garantia_estructural_anios: 10,
    garantia_estructural_meses: 0,
  };

  const [formData, setFormData] = useState(initialFormData);

  useEffect(() => {
    if (proyecto?.id) {
      loadSubdivisiones();
    }
  }, [proyecto?.id]);

  const loadSubdivisiones = async () => {
    try {
      const response = await subdivisionesAPI.getByProyecto(proyecto.id);
      setSubdivisiones(response.data || []);
    } catch (error) {
      toast.error('Error al cargar subdivisiones');
    } finally {
      setLoading(false);
    }
  };

  const organizeSubdivisiones = () => {
    const roots = subdivisiones.filter(s => !s.padre_id);
    const children = subdivisiones.filter(s => s.padre_id);

    const buildTree = (items) => {
      return items.map(item => ({
        ...item,
        children: buildTree(children.filter(c => c.padre_id === item.id))
      }));
    };

    return buildTree(roots);
  };

  const handleOpenCreate = (padreId = null) => {
    setEditingSubdivision(null);
    setFormData({
      ...initialFormData,
      codigo: proyecto?.codigo_proyecto ? `${proyecto.codigo_proyecto}-` : '',
      padre_id: padreId || '',
    });
    setShowDialog(true);
  };

  const handleOpenEdit = (subdivision) => {
    setEditingSubdivision(subdivision);
    setFormData({
      nombre: subdivision.nombre || '',
      codigo: subdivision.codigo || '',
      tipo_subdivision: subdivision.tipo_subdivision || '',
      numero_pisos: subdivision.numero_pisos || '',
      observaciones: subdivision.observaciones || '',
      padre_id: subdivision.padre_id || '',
      fecha_entrega: subdivision.fecha_entrega || '',
      fecha_inicio_garantia_acabados: subdivision.fecha_inicio_garantia_acabados || '',
      garantia_acabados_anios: subdivision.garantia_acabados_anios ?? 1,
      garantia_acabados_meses: subdivision.garantia_acabados_meses ?? 0,
      fecha_inicio_garantia_estructural: subdivision.fecha_inicio_garantia_estructural || '',
      garantia_estructural_anios: subdivision.garantia_estructural_anios ?? 10,
      garantia_estructural_meses: subdivision.garantia_estructural_meses ?? 0,
    });
    setShowDialog(true);
  };

  const handleSave = async () => {
    if (!formData.nombre.trim()) {
      toast.error('El nombre es obligatorio');
      return;
    }
    if (!formData.codigo.trim() || formData.codigo === `${proyecto?.codigo_proyecto}-`) {
      toast.error('El codigo es obligatorio');
      return;
    }
    if (!formData.tipo_subdivision) {
      toast.error('El tipo de subdivision es obligatorio');
      return;
    }

    setSaving(true);
    try {
      const data = {
        ...formData,
        proyecto_id: proyecto.id,
        numero_pisos: formData.numero_pisos ? parseInt(formData.numero_pisos) : null,
        padre_id: formData.padre_id || null,
        fecha_entrega: formData.fecha_entrega || null,
        garantia_acabados_anios: parseInt(formData.garantia_acabados_anios),
        garantia_acabados_meses: parseInt(formData.garantia_acabados_meses),
        garantia_estructural_anios: parseInt(formData.garantia_estructural_anios),
        garantia_estructural_meses: parseInt(formData.garantia_estructural_meses),
      };

      if (editingSubdivision) {
        await subdivisionesAPI.update(editingSubdivision.id, data);
        toast.success('Subdivision actualizada');
      } else {
        await subdivisionesAPI.create(proyecto.id, data);
        toast.success('Subdivision creada');
      }

      setShowDialog(false);
      loadSubdivisiones();
      if (onUpdate) onUpdate();
    } catch (error) {
      toast.error('Error al guardar');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Esta seguro de eliminar esta subdivision?')) return;

    try {
      await subdivisionesAPI.delete(id);
      toast.success('Subdivision eliminada');
      loadSubdivisiones();
    } catch (error) {
      toast.error('Error al eliminar');
    }
  };

  const handleCopiarFechaHijos = async (subdivisionId, nombre) => {
    setCopyingFecha(subdivisionId);
    try {
      const response = await subdivisionesAPI.copiarFechaHijos(subdivisionId);
      const result = response.data;
      toast.success(`Fecha copiada a ${result.actualizados} hijo(s) de "${nombre}"`);
      loadSubdivisiones();
      if (onUpdate) onUpdate();
    } catch (error) {
      const msg = error.response?.data?.detail || 'Error al copiar fecha';
      toast.error(msg);
    } finally {
      setCopyingFecha(null);
    }
  };

  const formatGarantia = (anios, meses) => {
    const parts = [];
    if (anios > 0) parts.push(`${anios}a`);
    if (meses > 0) parts.push(`${meses}m`);
    return parts.length > 0 ? parts.join(' ') : '0';
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return null;
    try {
      const d = new Date(dateStr + 'T00:00:00');
      return d.toLocaleDateString('es-PE', { day: '2-digit', month: '2-digit', year: 'numeric' });
    } catch {
      return dateStr;
    }
  };

  const organizedSubdivisiones = organizeSubdivisiones();

  // Check if a subdivision has children
  const hasChildren = (id) => subdivisiones.some(s => s.padre_id === id);

  if (loading) {
    return (
      <div className="flex items-center justify-center gap-2 py-8 text-sm text-[#888888]">
        <Loader2 size={18} className="animate-spin text-[#9d8272]" />
        <span>Cargando subdivisiones...</span>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h3 className="text-sm font-semibold text-[#2D2D2D] flex items-center gap-2">
          <Layers size={16} className="text-[#9d8272]" />
          Subdivisiones ({subdivisiones.length})
        </h3>
        <Button size="sm" onClick={() => handleOpenCreate()} className="h-8 bg-[#9d8272] hover:bg-[#8a7162] text-white" data-testid="agregar-subdivision-btn">
          <Plus size={14} className="mr-1" />
          Agregar
        </Button>
      </div>

      {organizedSubdivisiones.length > 0 ? (
        <div className="space-y-3">
          {organizedSubdivisiones.map(etapa => (
            <div key={etapa.id} className="border border-[#c5b8a2]/40 rounded-lg overflow-hidden bg-[#faf8f5]">
              {/* Etapa header */}
              <div className="flex items-center justify-between px-4 py-2.5 bg-[#f0ece6] border-b border-[#c5b8a2]/30">
                <div className="flex items-center gap-2 flex-1 min-w-0">
                  <button
                    onClick={() => handleOpenEdit(etapa)}
                    className="flex items-center gap-2 text-sm font-semibold text-[#5a4a3a] hover:text-[#9d8272] hover:underline transition-colors"
                    data-testid={`etapa-link-${etapa.id}`}
                  >
                    <Layers size={15} className="text-[#9d8272] flex-shrink-0" />
                    {etapa.nombre}
                    <Badge variant="outline" className="text-[10px] font-normal">{etapa.tipo_subdivision}</Badge>
                  </button>
                  {etapa.fecha_entrega && (
                    <span className="text-[10px] text-blue-600 bg-blue-50 px-1.5 py-0.5 rounded border border-blue-100 flex-shrink-0" data-testid={`fecha-entrega-etapa-${etapa.id}`}>
                      <Calendar size={10} className="inline mr-0.5" />
                      {formatDate(etapa.fecha_entrega)}
                    </span>
                  )}
                </div>
                <div className="flex items-center gap-1">
                  {/* Copy date to children button - only if has fecha_entrega and has children */}
                  {etapa.fecha_entrega && etapa.children && etapa.children.length > 0 && (
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleCopiarFechaHijos(etapa.id, etapa.nombre)}
                      disabled={copyingFecha === etapa.id}
                      className="h-7 px-2 text-blue-600 hover:text-blue-800 hover:bg-blue-50"
                      title="Copiar fecha a hijos"
                      data-testid={`copiar-fecha-hijos-${etapa.id}`}
                    >
                      {copyingFecha === etapa.id ? (
                        <Loader2 size={14} className="animate-spin" />
                      ) : (
                        <Copy size={14} />
                      )}
                    </Button>
                  )}
                  <Button variant="ghost" size="sm" onClick={() => handleOpenCreate(etapa.id)} className="h-7 w-7 p-0" title="Agregar bloque">
                    <Plus size={14} />
                  </Button>
                  <Button variant="ghost" size="sm" onClick={() => handleOpenEdit(etapa)} className="h-7 w-7 p-0" title="Editar">
                    <Edit size={14} />
                  </Button>
                  <Button variant="ghost" size="sm" onClick={() => handleDelete(etapa.id)} className="h-7 w-7 p-0 text-red-500 hover:text-red-700" title="Eliminar">
                    <Trash2 size={14} />
                  </Button>
                </div>
              </div>
              {/* Bloques grid */}
              {etapa.children && etapa.children.length > 0 ? (
                <div className="flex flex-wrap gap-2 p-3">
                  {etapa.children.map(bloque => (
                    <div key={bloque.id} className="border border-[#E8E8E7] rounded-md bg-white px-3 py-2 flex items-center gap-2 hover:shadow-sm transition-shadow group">
                      <button
                        onClick={() => handleOpenEdit(bloque)}
                        className="text-sm text-[#2D2D2D] hover:text-[#9d8272] hover:underline transition-colors font-medium"
                        data-testid={`bloque-link-${bloque.id}`}
                      >
                        {bloque.nombre}
                      </button>
                      <Badge variant="outline" className="text-[9px]">{bloque.tipo_subdivision}</Badge>
                      {bloque.fecha_entrega && (
                        <span className="text-[9px] text-blue-600 bg-blue-50 px-1 py-0.5 rounded" data-testid={`fecha-entrega-bloque-${bloque.id}`}>
                          {formatDate(bloque.fecha_entrega)}
                        </span>
                      )}
                      <div className="flex items-center gap-0.5 opacity-0 group-hover:opacity-100 transition-opacity">
                        <Button variant="ghost" size="sm" onClick={() => handleOpenEdit(bloque)} className="h-6 w-6 p-0">
                          <Edit size={12} />
                        </Button>
                        <Button variant="ghost" size="sm" onClick={() => handleDelete(bloque.id)} className="h-6 w-6 p-0 text-red-500 hover:text-red-700">
                          <Trash2 size={12} />
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="px-4 py-3 text-xs text-[#888888] italic">Sin bloques registrados</div>
              )}
            </div>
          ))}
        </div>
      ) : (
        <div className="text-center py-6 text-sm text-[#888888] bg-[#F5F5F5] rounded-lg">
          No hay subdivisiones registradas
        </div>
      )}

      {/* Dialog para crear/editar subdivision */}
      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent className="max-w-lg w-[95vw] max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-lg font-bold text-[#2D2D2D]">
              {editingSubdivision ? 'Editar Subdivision' : 'Nueva Subdivision'}
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-4 mt-4">
            {/* Datos Basicos */}
            <div className="bg-[#F5F5F5] p-4 rounded-lg space-y-3">
              <Label className="text-sm font-semibold text-[#9d8272]">Datos Basicos</Label>

              <div className="grid grid-cols-2 gap-3">
                <div className="col-span-2">
                  <Label className="text-xs">Nombre *</Label>
                  <Input
                    value={formData.nombre}
                    onChange={(e) => setFormData({ ...formData, nombre: e.target.value.slice(0, 30) })}
                    className="border-[#c5b8a2] mt-1 h-9 text-sm"
                    maxLength={30}
                    placeholder="Nombre de la subdivision"
                    data-testid="subdivision-nombre-input"
                  />
                </div>

                <div>
                  <Label className="text-xs">Codigo * (max 40 car.)</Label>
                  <Input
                    value={formData.codigo}
                    onChange={(e) => setFormData({ ...formData, codigo: e.target.value.slice(0, 40) })}
                    className="border-[#c5b8a2] mt-1 h-9 text-sm font-mono"
                    maxLength={40}
                    placeholder={`${proyecto?.codigo_proyecto || 'CP'}-XXX`}
                    data-testid="subdivision-codigo-input"
                  />
                </div>

                <div>
                  <Label className="text-xs">Tipo de Subdivision *</Label>
                  <Select value={formData.tipo_subdivision} onValueChange={(v) => setFormData({ ...formData, tipo_subdivision: v })}>
                    <SelectTrigger className="border-[#c5b8a2] mt-1 h-9 text-sm" data-testid="subdivision-tipo-select">
                      <SelectValue placeholder="Seleccione..." />
                    </SelectTrigger>
                    <SelectContent>
                      {TIPOS_SUBDIVISION.map(tipo => (
                        <SelectItem key={tipo} value={tipo}>{tipo}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <Label className="text-xs">Numero de Pisos</Label>
                  <Input
                    type="number"
                    value={formData.numero_pisos}
                    onChange={(e) => setFormData({ ...formData, numero_pisos: e.target.value })}
                    className="border-[#c5b8a2] mt-1 h-9 text-sm"
                    min="0"
                    data-testid="subdivision-pisos-input"
                  />
                </div>

                <div>
                  <Label className="text-xs">Pertenece a</Label>
                  <Select value={formData.padre_id || "none"} onValueChange={(v) => setFormData({ ...formData, padre_id: v === "none" ? "" : v })}>
                    <SelectTrigger className="border-[#c5b8a2] mt-1 h-9 text-sm" data-testid="subdivision-padre-select">
                      <SelectValue placeholder="Ninguna (raiz)" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">Ninguna (raiz)</SelectItem>
                      {subdivisiones
                        .filter(s => s.id !== editingSubdivision?.id)
                        .map(s => (
                          <SelectItem key={s.id} value={s.id}>{s.codigo} - {s.nombre}</SelectItem>
                        ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="col-span-2">
                  <Label className="text-xs">Observaciones</Label>
                  <Textarea
                    value={formData.observaciones}
                    onChange={(e) => setFormData({ ...formData, observaciones: e.target.value.slice(0, 1000) })}
                    className="border-[#c5b8a2] mt-1 text-sm"
                    rows={2}
                    maxLength={1000}
                    placeholder="Observaciones adicionales..."
                    data-testid="subdivision-observaciones-input"
                  />
                </div>
              </div>
            </div>

            {/* Fecha de Entrega */}
            <div className="bg-blue-50 p-4 rounded-lg border border-blue-100 space-y-2">
              <Label className="text-sm font-semibold text-blue-800 flex items-center gap-2">
                <Calendar size={14} />
                Fecha de Entrega
              </Label>
              <p className="text-xs text-blue-600">Campo opcional</p>
              <Input
                type="date"
                value={formData.fecha_entrega}
                onChange={(e) => setFormData({ ...formData, fecha_entrega: e.target.value })}
                className="border-blue-200 bg-white h-9 text-sm"
                data-testid="subdivision-fecha-entrega-input"
              />
              {/* Show "copy to children" hint when editing a parent with fecha_entrega */}
              {editingSubdivision && formData.fecha_entrega && hasChildren(editingSubdivision.id) && (
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => {
                    handleCopiarFechaHijos(editingSubdivision.id, editingSubdivision.nombre);
                  }}
                  disabled={copyingFecha === editingSubdivision?.id}
                  className="mt-1 text-blue-700 border-blue-200 hover:bg-blue-100 h-8 text-xs"
                  data-testid="copiar-fecha-hijos-dialog-btn"
                >
                  {copyingFecha === editingSubdivision?.id ? (
                    <Loader2 size={12} className="mr-1 animate-spin" />
                  ) : (
                    <Copy size={12} className="mr-1" />
                  )}
                  Copiar fecha a hijos
                </Button>
              )}
            </div>

            {/* Garantia */}
            <div className="bg-[#F5F5F5] p-4 rounded-lg space-y-3">
              <Label className="text-sm font-semibold text-[#9d8272] flex items-center gap-2">
                <Shield size={14} />
                Garantia
              </Label>

              {/* Garantia Acabados */}
              <div className="grid grid-cols-3 gap-2">
                <div className="col-span-3">
                  <Label className="text-[10px] text-[#8c926f] font-medium">Garantia de Acabados</Label>
                </div>
                <div>
                  <Label className="text-[10px] text-[#888888]">Fecha Inicio</Label>
                  <Input
                    type="date"
                    value={formData.fecha_inicio_garantia_acabados}
                    onChange={(e) => setFormData({ ...formData, fecha_inicio_garantia_acabados: e.target.value })}
                    className="border-[#c5b8a2] mt-1 h-9 text-sm"
                  />
                </div>
                <div>
                  <Label className="text-[10px] text-[#888888]">Anos</Label>
                  <Select value={String(formData.garantia_acabados_anios)} onValueChange={(v) => setFormData({ ...formData, garantia_acabados_anios: parseInt(v) })}>
                    <SelectTrigger className="border-[#c5b8a2] mt-1 h-9 text-sm">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {[...Array(11)].map((_, i) => (
                        <SelectItem key={i} value={String(i)}>{i}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-[10px] text-[#888888]">Meses</Label>
                  <Select value={String(formData.garantia_acabados_meses)} onValueChange={(v) => setFormData({ ...formData, garantia_acabados_meses: parseInt(v) })}>
                    <SelectTrigger className="border-[#c5b8a2] mt-1 h-9 text-sm">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {[...Array(12)].map((_, i) => (
                        <SelectItem key={i} value={String(i)}>{i}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              {/* Garantia Estructural */}
              <div className="grid grid-cols-3 gap-2">
                <div className="col-span-3">
                  <Label className="text-[10px] text-[#9d8272] font-medium">Garantia Estructural</Label>
                </div>
                <div>
                  <Label className="text-[10px] text-[#888888]">Fecha Inicio</Label>
                  <Input
                    type="date"
                    value={formData.fecha_inicio_garantia_estructural}
                    onChange={(e) => setFormData({ ...formData, fecha_inicio_garantia_estructural: e.target.value })}
                    className="border-[#c5b8a2] mt-1 h-9 text-sm"
                  />
                </div>
                <div>
                  <Label className="text-[10px] text-[#888888]">Anos</Label>
                  <Select value={String(formData.garantia_estructural_anios)} onValueChange={(v) => setFormData({ ...formData, garantia_estructural_anios: parseInt(v) })}>
                    <SelectTrigger className="border-[#c5b8a2] mt-1 h-9 text-sm">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {[...Array(11)].map((_, i) => (
                        <SelectItem key={i} value={String(i)}>{i}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-[10px] text-[#888888]">Meses</Label>
                  <Select value={String(formData.garantia_estructural_meses)} onValueChange={(v) => setFormData({ ...formData, garantia_estructural_meses: parseInt(v) })}>
                    <SelectTrigger className="border-[#c5b8a2] mt-1 h-9 text-sm">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {[...Array(12)].map((_, i) => (
                        <SelectItem key={i} value={String(i)}>{i}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </div>

            {/* Botones */}
            <div className="flex justify-end gap-3 pt-2">
              <Button variant="outline" onClick={() => setShowDialog(false)} size="sm" data-testid="subdivision-cancelar-btn">
                Cancelar
              </Button>
              <Button onClick={handleSave} disabled={saving} size="sm" className="bg-[#9d8272] hover:bg-[#8a7162] text-white" data-testid="subdivision-guardar-btn">
                {saving ? 'Guardando...' : editingSubdivision ? 'Actualizar' : 'Crear'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default SubdivisionesManager;
